/**
 * aiTaskManager.js (client)
 * Gestion du cycle de vie des tâches IA en arrière-plan :
 * pending → running → completed | failed | cancelled
 * Fournit également un modal de détail, la reprise et l'annulation.
 */

import { trackAiRequest, getChannelInfo } from './aiRequestTracker';

const TASKS_STORAGE_KEY = 'ony_ai_tasks';
const MAX_TASKS = 50;

const eventTarget = new EventTarget();

function getApiBase() {
  return process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
}

function getStoredTasks() {
  try {
    const raw = localStorage.getItem(TASKS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveTasks(tasks) {
  try {
    localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(tasks.slice(0, MAX_TASKS)));
  } catch {
    // ignore
  }
}

function dispatch(eventName, detail) {
  eventTarget.dispatchEvent(new CustomEvent(eventName, { detail }));
}

function generateId() {
  return `task-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function migrateTask(task) {
  if (!task) return task;
  return {
    history: [],
    context: {},
    outputs: null,
    error: null,
    ...task,
  };
}

export function createTask(payload = {}) {
  const id = payload.id || generateId();
  const channel = payload.channelId || 'generic';
  const info = getChannelInfo(channel);
  const now = Date.now();

  const task = migrateTask({
    ...payload,
    id,
    status: 'pending',
    channelId: channel,
    channelName: payload.channelName || info.name,
    channelColor: payload.channelColor || info.color,
    request: payload.request || '',
    response: payload.response || null,
    history: payload.history || [],
    context: payload.context || {},
    outputs: payload.outputs || null,
    error: null,
    url: payload.url || null,
    createdAt: now,
    updatedAt: now,
  });

  const tasks = getStoredTasks();
  tasks.unshift(task);
  saveTasks(tasks);

  trackAiRequest({
    id,
    channelId: task.channelId,
    channelName: task.channelName,
    channelColor: task.channelColor,
    request: task.request,
    response: task.response,
    timestamp: now,
    status: 'pending',
  });

  dispatch('ai-task-updated', task);
  return task;
}

export function updateTask(id, updates = {}) {
  const tasks = getStoredTasks();
  const idx = tasks.findIndex(t => t.id === id);
  if (idx === -1) return null;

  const existing = migrateTask(tasks[idx]);
  const task = { ...existing, ...updates, id, updatedAt: Date.now() };
  tasks[idx] = task;
  saveTasks(tasks);

  trackAiRequest({
    id,
    channelId: task.channelId,
    channelName: task.channelName,
    channelColor: task.channelColor,
    request: task.request,
    response: task.response,
    timestamp: task.updatedAt,
    status: task.status,
    history: task.history,
    context: task.context,
    outputs: task.outputs,
    error: task.error,
  });

  dispatch('ai-task-updated', task);
  return task;
}

export function getTask(id) {
  const task = getStoredTasks().find(t => t.id === id);
  return task ? migrateTask(task) : null;
}

export function getAllTasks() {
  return getStoredTasks().map(migrateTask);
}

export function removeTask(id) {
  const tasks = getStoredTasks().filter(t => t.id !== id);
  saveTasks(tasks);
  dispatch('ai-task-removed', { id });
}

export async function cancelTask(id) {
  const task = getTask(id);
  if (!task) return { success: false, error: 'Tâche non trouvée' };
  if (['completed', 'failed', 'cancelled'].includes(task.status)) {
    return { success: true, alreadyDone: true };
  }

  updateTask(id, { status: 'cancelled' });

  try {
    const res = await fetch(`${getApiBase()}/nova/tasks/${id}/cancel`, { method: 'POST' });
    const data = await res.json();
    if (!data.success) {
      updateTask(id, { status: task.status });
      return { success: false, error: data.error || 'Échec de l\'annulation' };
    }
    updateTask(id, { status: 'cancelled', error: 'Annulée par l\'utilisateur' });
    return { success: true };
  } catch (err) {
    updateTask(id, { status: task.status });
    return { success: false, error: err.message };
  }
}

export function resumeInAIHub(taskId, navigate) {
  const task = getTask(taskId);
  if (!task) return false;
  if (typeof navigate !== 'function') return false;

  const historyParam = encodeURIComponent(JSON.stringify(task.history || []));
  const contextParam = encodeURIComponent(JSON.stringify(task.context || {}));
  navigate(`/aihub?channel=${encodeURIComponent(task.channelId || 'generic')}&message=${encodeURIComponent(task.request || '')}&history=${historyParam}&context=${contextParam}&taskId=${task.id}`);
  return true;
}

export function subscribeTasks(callback) {
  const handler = e => callback(e.detail);
  eventTarget.addEventListener('ai-task-updated', handler);
  return () => eventTarget.removeEventListener('ai-task-updated', handler);
}

export function subscribeTaskRemoved(callback) {
  const handler = e => callback(e.detail);
  eventTarget.addEventListener('ai-task-removed', handler);
  return () => eventTarget.removeEventListener('ai-task-removed', handler);
}

export function getFullMessageHistory(taskId) {
  const task = getTask(taskId);
  if (!task) return [];
  if (Array.isArray(task.history) && task.history.length > 0) {
    return task.history;
  }
  const msgs = [];
  if (task.request) msgs.push({ role: 'user', content: task.request });
  if (task.response) msgs.push({ role: 'assistant', content: task.response });
  return msgs;
}

export function isTaskRunning(status) {
  return status === 'pending' || status === 'running';
}

export function isTaskFinished(status) {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

export function getOutputSummary(task = {}) {
  const channel = task.channelId || 'generic';
  const outputs = task.outputs || task.response || task.specialContent || null;
  return { channel, outputs };
}
